'use client'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Suspense, useCallback, useEffect, useState } from 'react'
import {
  AuthDemoFooter,
  AuthDivider,
  AuthError,
  AuthField,
  AuthGoogleButton,
  AuthHeading,
  AuthPasswordField,
  AuthPrimaryButton,
} from '@/components/auth/auth-form-primitives'
import { AuthShell } from '@/components/auth/auth-shell'
import { WORKSPACE_LOGIN_PAUSED_ERROR } from '@/lib/auth/workspace-login-pause'
import { createClient } from '@/lib/supabase/client'
import { LoginCheckingState } from './login-checking-state'

interface PreviewUser {
  email: string
  id: string
  name: string
}

type QuickLoginMode = 'development' | 'preview'

function getSafeNextUrl(value: string | null): string {
  if (!value?.startsWith('/') || value.startsWith('//')) {
    return '/'
  }

  try {
    const url = new URL(value, 'https://trellistech.local')
    if (url.origin !== 'https://trellistech.local' || url.pathname === '/login') {
      return '/'
    }
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}

function getLoginErrorMessage(value: string | null, workspaceLoginPausedMessage: string) {
  if (value === WORKSPACE_LOGIN_PAUSED_ERROR) {
    return workspaceLoginPausedMessage
  }
  return value
}

function isNetworkError(error: unknown): boolean {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('message' in error) ||
    typeof error.message !== 'string'
  ) {
    return false
  }

  const message = error.message.trim()
  return message === 'Failed to fetch' || message.startsWith('Failed to fetch (')
}

async function signInThroughServer(email: string, password: string): Promise<string | null> {
  const response = await fetch('/api/auth/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  const payload = (await response.json().catch(() => null)) as { error?: string } | null

  if (!response.ok) {
    return payload?.error ?? 'Authentication failed'
  }
  return null
}

function LoginForm() {
  const t = useTranslations('Auth')
  const tPage = useTranslations('PageTitles')
  const router = useRouter()
  const searchParams = useSearchParams()
  const nextUrl = getSafeNextUrl(searchParams.get('next'))
  const urlError = searchParams.get('error')
  const supabase = createClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [previewUsers, setPreviewUsers] = useState<PreviewUser[]>([])
  const [previewUser, setPreviewUser] = useState('')
  const [quickLoginMode, setQuickLoginMode] = useState<QuickLoginMode>('preview')
  const [error, setError] = useState<string | null>(
    getLoginErrorMessage(urlError, t('workspaceLoginPaused'))
  )
  const [rejectedAttempts, setRejectedAttempts] = useState(0)
  const [lastErrorIsCredentialRejection, setLastErrorIsCredentialRejection] = useState(false)

  useEffect(() => {
    let cancelled = false

    const checkAuth = async () => {
      try {
        if (urlError === WORKSPACE_LOGIN_PAUSED_ERROR) {
          await supabase.auth.signOut()
          return
        }

        const result = await supabase.auth.getUser()
        if (cancelled) {
          return
        }

        const {
          data: { user },
        } = result

        if (user) {
          router.refresh()
          router.push(nextUrl)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Auth check error:', err)
        }
      }
    }

    checkAuth()
    return () => {
      cancelled = true
    }
  }, [router, supabase.auth, nextUrl, urlError])

  useEffect(() => {
    fetch('/api/auth/preview-login', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: { mode?: QuickLoginMode; users?: PreviewUser[] } | null) => {
        const users = payload?.users ?? []
        setPreviewUsers(users)
        setPreviewUser(users[0]?.id ?? '')
        if (payload?.mode) {
          setQuickLoginMode(payload.mode)
        }
      })
      .catch(() => undefined)
  }, [])

  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsLoading(true)
      setError(null)

      try {
        let loginError: unknown = null

        try {
          const { error: directSignInError } = await supabase.auth.signInWithPassword({
            email,
            password,
          })
          loginError = directSignInError
          if (isNetworkError(loginError)) {
            loginError = await signInThroughServer(email, password)
          }
        } catch (directError) {
          if (!isNetworkError(directError)) {
            throw directError
          }
          loginError = await signInThroughServer(email, password)
        }

        if (loginError) {
          console.error('[auth] Login rejected', loginError)
          if (isRejectedCredential(loginError)) {
            setRejectedAttempts((count) => count + 1)
            setLastErrorIsCredentialRejection(true)
            setError(t('loginRejected'))
          } else {
            setLastErrorIsCredentialRejection(false)
            setError(t('loginFailed'))
          }
          setIsLoading(false)
          return
        }

        location.assign(nextUrl)
        setIsLoading(false)
      } catch (err: unknown) {
        console.error('Login failed:', err)
        setLastErrorIsCredentialRejection(false)
        setError(t('loginFailed'))
        setIsLoading(false)
      }
    },
    [email, nextUrl, password, supabase.auth, t]
  )

  const handleGoogleSignIn = useCallback(async () => {
    setIsLoading(true)
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextUrl)}`,
      },
    })
  }, [nextUrl, supabase.auth])

  const handleQuickSignIn = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    const response = await fetch('/api/auth/preview-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user: previewUser }),
    })
    const payload = (await response.json().catch(() => null)) as { error?: string } | null
    if (!response.ok) {
      setError(payload?.error ?? t('loginFailed'))
      setIsLoading(false)
      return
    }
    location.assign(nextUrl)
  }, [nextUrl, previewUser, t])

  const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    // A new email targets a different account, so a failure streak counted
    // against the old address shouldn't carry over and prematurely suggest
    // a reset for one the person hasn't even tried yet.
    setRejectedAttempts(0)
    setLastErrorIsCredentialRejection(false)
  }, [])
  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value),
    []
  )
  const handlePreviewUserChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setPreviewUser(event.target.value),
    []
  )

  return (
    <AuthShell>
      <form
        action="/api/auth/password"
        method="post"
        onSubmit={handleLogin}
        style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}
      >
        <AuthHeading subtitle={t('signInToContinue')} title={tPage('welcomeBack')} />

        <AuthField
          autoComplete="email"
          data-testid="login-email-input"
          id="email"
          label={t('emailLabel')}
          name="email"
          onChange={handleEmailChange}
          placeholder={t('emailPlaceholder')}
          required
          type="email"
          value={email}
        />

        <input name="next" type="hidden" value={nextUrl} />

        <AuthPasswordField
          autoComplete="current-password"
          data-testid="login-password-input"
          id="password"
          label={t('passwordLabel')}
          name="password"
          onChange={handlePasswordChange}
          placeholder={t('passwordPlaceholder')}
          required
          trailingLabel={
            <Link
              href="/forgot-password"
              style={{
                fontFamily: 'var(--font-geist), ui-sans-serif, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                color: 'var(--ds-text-link)',
                textDecoration: 'none',
              }}
            >
              {t('forgotPassword')}
            </Link>
          }
          value={password}
        />

        {error ? (
          <AuthError>
            {error}
            {lastErrorIsCredentialRejection && rejectedAttempts >= 2 ? (
              <>
                {' '}
                <Link
                  href={`/forgot-password?email=${encodeURIComponent(email)}`}
                  style={{ color: 'inherit', fontWeight: 700 }}
                >
                  {t('resetPasswordCta')}
                </Link>
              </>
            ) : null}
          </AuthError>
        ) : null}

        <AuthPrimaryButton
          data-testid="login-submit-button"
          disabled={isLoading}
          isLoading={isLoading}
          type="submit"
        >
          {isLoading ? t('signingIn') : t('signIn')}
        </AuthPrimaryButton>

        <AuthDivider label={t('orContinueWith')} />

        <AuthGoogleButton
          disabled={isLoading}
          label={t('google')}
          onClick={handleGoogleSignIn}
          type="button"
        />

        {previewUsers.length > 0 ? (
          <div className="flex flex-col gap-3">
            {previewUsers.length > 1 ? (
              <>
                <label className="text-bold-sm" htmlFor="preview-user">
                  {t('previewUser')}
                </label>
                <select
                  className="h-12 rounded-xl border border-border bg-card px-3 text-control outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand-soft md:text-body-sm"
                  disabled={isLoading}
                  id="preview-user"
                  onChange={handlePreviewUserChange}
                  value={previewUser}
                >
                  {previewUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            <AuthPrimaryButton disabled={isLoading} onClick={handleQuickSignIn} type="button">
              {quickLoginMode === 'development' ? t('developmentSignIn') : t('previewSignIn')}
            </AuthPrimaryButton>
          </div>
        ) : null}

        <AuthDemoFooter href="/signup" linkLabel={t('signUp')} prompt={t('dontHaveAccount')} />
      </form>
    </AuthShell>
  )
}

/**
 * Supabase answers a wrong password and a missing account with the same
 * `invalid_credentials`, on purpose, so nobody can probe which addresses are
 * registered. Both are the person's problem to fix; a network or provider
 * failure is not. Saying "the authentication service returned an invalid
 * response" to someone who simply mistyped sends them hunting an outage —
 * which is exactly what happened before this existed.
 */
function isRejectedCredential(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const code = Reflect.get(error, 'code')
  if (code === 'invalid_credentials' || code === 'email_not_confirmed') {
    return true
  }
  return Reflect.get(error, 'status') === 400
}

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginCheckingState />}>
      <LoginForm />
    </Suspense>
  )
}
